import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IllustrationListItem } from "@src/appV2/redesign/components/IllustrationListItem";
import { useEffect, useRef } from "react";

export interface StripeSetupTipsBottomSheetProps {
  modalState: UseModalState;
  onClosed: () => void;
}

export function StripeSetupTipsBottomSheet(props: StripeSetupTipsBottomSheetProps) {
  const { modalState, onClosed } = props;
  const wasOpened = useRef(false);

  useLogEffect(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_TIPS_VIEWED, undefined, {
    enabled: modalState.modalIsOpen,
  });

  useEffect(() => {
    if (modalState.modalIsOpen) {
      wasOpened.current = true;
    }
  }, [modalState.modalIsOpen]);

  useEffect(() => {
    if (modalState.modalIsClosed && wasOpened.current) {
      wasOpened.current = false;
      onClosed();
    }
  }, [modalState.modalIsClosed, onClosed]);

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      footer={
        <DialogFooter orientation="vertical">
          <Button fullWidth onClick={modalState.closeModal}>
            Continue
          </Button>
        </DialogFooter>
      }
    >
      <Stack spacing={10} sx={(theme) => ({ paddingX: theme.spacing(5) })}>
        <Stack
          alignItems="center"
          sx={(theme) => ({
            paddingTop: theme.spacing(5),
          })}
          spacing={7}
        >
          <Illustration type="lightbulb-gray" />
          <Text semibold variant="h4" sx={{ textAlign: "center" }}>
            Tips for setting up Stripe
          </Text>
        </Stack>
        <Stack>
          <IllustrationListItem
            divider
            illustrationType="key-gray-small"
            title="Save your Stripe login credentials"
            description="You'll need these to manage your payout destinations in the future. Save them in a password manager or somewhere secure."
          />
          <IllustrationListItem
            divider
            illustrationType="bank-gray-small"
            title="Add a backup payout destination"
            description="We recommend adding at least 2 payout destinations in case one has issues."
          />
          <IllustrationListItem
            illustrationType="debit-card-gray-small"
            title="Link an instant-eligible card or bank"
            description='In order to be eligible for InstantPay, make sure you see a green "Instant-eligible" label next to your payout destination in Stripe.'
          />
        </Stack>
      </Stack>
    </BottomSheet>
  );
}
